import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Chart } from 'chart.js/auto';
import { Activity, ArrowDownToLine, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, FileText, LucideAngularModule, RefreshCcw, TrendingUp, TriangleAlert } from 'lucide-angular';
import { forkJoin } from 'rxjs';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { ReaMatchingDetail, ReaMatchingRow, ReaReport } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { ReaReportsService } from '../../rea-reports/services/rea-reports.service';

@Component({
  standalone: true,
  selector: 'app-rea-matching-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './rea-matching.page.html',
  styleUrl: './rea-matching.page.scss',
})
export class ReaMatchingPage implements OnInit, AfterViewInit, OnDestroy {
  private readonly service = inject(ReaReportsService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  private comparisonCanvas?: ElementRef<HTMLCanvasElement>;

  @ViewChild('comparisonCanvas')
  set comparisonCanvasRef(value: ElementRef<HTMLCanvasElement> | undefined) {
    this.comparisonCanvas = value;
    if (value) {
      this.scheduleChartRender();
    }
  }

  private comparisonChart?: Chart;

  protected readonly ActivityIcon = Activity;
  protected readonly ArrowDownToLineIcon = ArrowDownToLine;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly DownloadIcon = Download;
  protected readonly FileTextIcon = FileText;
  protected readonly RefreshCcwIcon = RefreshCcw;
  protected readonly TrendingUpIcon = TrendingUp;
  protected readonly TriangleAlertIcon = TriangleAlert;

  protected readonly reportsLoading = signal(false);
  protected readonly matchingLoading = signal(false);
  protected readonly savedReports = signal<ReaReport[]>([]);
  protected readonly selectedReportId = signal<string>('');
  protected readonly selectedYear = signal<string>('');
  protected readonly selectedMonth = signal<string>('');
  protected readonly matching = signal<ReaMatchingDetail | null>(null);
  protected readonly page = signal(1);
  protected readonly pageSize = signal<number>(DEFAULT_PAGE_SIZE_OPTIONS[0] || 10);
  protected readonly pageSizes = DEFAULT_PAGE_SIZE_OPTIONS;

  protected readonly yearOptions = computed(() =>
    Array.from(
      new Set(
        this.savedReports()
          .map((report) => String(report.report_year || ''))
          .filter((value) => !!value),
      ),
    ).sort((a, b) => Number(b) - Number(a)),
  );

  protected readonly monthOptions = computed(() => Array.from({ length: 12 }, (_, index) => index + 1));

  protected readonly filteredReports = computed(() =>
    this.savedReports().filter((report) => {
      const yearMatch = !this.selectedYear() || String(report.report_year || '') === this.selectedYear();
      const monthMatch = !this.selectedMonth() || String(report.report_month || '') === this.selectedMonth();
      return yearMatch && monthMatch;
    }),
  );

  protected readonly totalRows = computed(() => this.matching()?.rows.length || 0);
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalRows() / this.pageSize())));
  protected readonly currentPage = computed(() => Math.min(this.page(), this.totalPages()));
  protected readonly pagedRows = computed(() => {
    const rows = this.matching()?.rows || [];
    const start = (this.currentPage() - 1) * this.pageSize();
    return rows.slice(start, start + this.pageSize());
  });
  protected readonly startRow = computed(() => (this.totalRows() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1));
  protected readonly endRow = computed(() => Math.min(this.currentPage() * this.pageSize(), this.totalRows()));

  protected readonly selectedReport = computed(
    () => this.savedReports().find((report) => report.id === this.selectedReportId()) || null,
  );

  protected readonly processSteps = computed(() => {
    const detail = this.matching();
    const highVariance = detail?.summary.high_variance_alerts || 0;
    return [
      { step: 1, label: 'Scheduled Energy', subtitle: 'From approved contracts', status: 'done' },
      { step: 2, label: 'REA from RLDC', subtitle: 'Actual metered energy', status: 'done' },
      {
        step: 3,
        label: 'Variance Analysis',
        subtitle: highVariance > 0 ? `${highVariance} rows need review` : 'Deviation within threshold',
        status: highVariance > 0 ? 'warning' : 'done',
      },
      { step: 4, label: 'Bill Adjustment', subtitle: 'Provisional vs final settlement', status: 'done' },
      { step: 5, label: 'Reconciled', subtitle: 'Contract level matching', status: 'done' },
    ];
  });

  ngOnInit(): void {
    this.loadSavedReports();
  }

  ngAfterViewInit(): void {
    this.scheduleChartRender();
  }

  ngOnDestroy(): void {
    this.comparisonChart?.destroy();
  }

  protected refresh(): void {
    this.loadSavedReports(true);
  }

  protected onYearChange(value: string): void {
    this.selectedYear.set(value);
    this.page.set(1);
    if (this.selectedMonth() && !this.monthOptions().includes(Number(this.selectedMonth()))) {
      this.selectedMonth.set('');
    }
    this.applyReportFilter(true);
  }

  protected onMonthChange(value: string): void {
    this.selectedMonth.set(value);
    this.page.set(1);
    this.applyReportFilter(true);
  }

  protected onPageSizeChange(value: string): void {
    const parsed = Number(value);
    const nextSize = this.pageSizes.find((size) => size === parsed) ?? this.pageSizes[0] ?? 10;
    this.pageSize.set(nextSize);
    this.page.set(1);
  }

  protected openAutoFetch(): void {
    const reportRegion = this.selectedReport()?.rpc_region?.toUpperCase();
    const path =
      reportRegion === 'NRPC'
        ? '/auto-fetch/nrpc-rea-reports'
        : reportRegion === 'NERPC'
          ? '/auto-fetch/nerpc-rea-reports'
          : '/auto-fetch/rea-reports';
    this.router.navigateByUrl(path);
  }

  protected downloadSelectedReport(): void {
    const report = this.selectedReport();
    if (!report) {
      return;
    }
    window.open(this.service.downloadUrl(report.id), '_blank');
  }

  protected metricClass(value: number): string {
    if (value > 0) {
      return 'metric-value metric-value-positive';
    }
    if (value < 0) {
      return 'metric-value metric-value-negative';
    }
    return 'metric-value';
  }

  protected metricIcon(type: 'scheduled' | 'actual' | 'deviation' | 'reconciled' | 'alerts') {
    if (type === 'deviation' || type === 'alerts') {
      return this.TriangleAlertIcon;
    }
    if (type === 'reconciled') {
      return this.FileTextIcon;
    }
    return this.TrendingUpIcon;
  }

  protected statusClass(row: ReaMatchingRow): string {
    if (row.status === 'Reconciled') {
      return 'status-pill status-pill-good';
    }
    if (row.status === 'Variance Review') {
      return 'status-pill status-pill-warn';
    }
    return 'status-pill status-pill-bad';
  }

  protected suggestedNoteLabel(row: ReaMatchingRow): string {
    if (row.adjustment > 0) {
      return 'Debit Note';
    }
    if (row.adjustment < 0) {
      return 'Credit Note';
    }
    return 'No Note';
  }

  protected suggestedNoteClass(row: ReaMatchingRow): string {
    if (row.adjustment > 0) {
      return 'note-pill note-pill-debit';
    }
    if (row.adjustment < 0) {
      return 'note-pill note-pill-credit';
    }
    return 'note-pill note-pill-neutral';
  }

  protected openSuggestedNote(row: ReaMatchingRow): void {
    const detail = this.matching();
    if (!detail || row.adjustment === 0) {
      return;
    }

    const noteType = row.adjustment > 0 ? 'Debit Note' : 'Credit Note';
    const month = detail.report.report_month ? this.monthLabel(detail.report.report_month) : 'NA';

    this.router.navigate(['/credit-debit-notes/new'], {
      queryParams: {
        source: 'rea-reconciliation',
        noteType,
        clientName: row.client_name || row.contract_number,
        remarks: `Auto-filled from REA conciliation for ${detail.report.report_type} ${month} ${detail.report.report_year}.`,
        particulars: `${detail.report.report_type} REA adjustment for contract ${row.contract_number}`,
        quantity: 1,
        rate: Math.abs(row.adjustment),
        amount: Math.abs(row.adjustment),
      },
    });
  }

  protected formatPeriod(report?: ReaReport | null): string {
    if (!report?.report_month || !report.report_year) {
      return 'Unknown period';
    }
    return new Date(report.report_year, report.report_month - 1, 1).toLocaleString(undefined, {
      month: 'long',
      year: 'numeric',
    });
  }

  protected formatNumber(value: number | null | undefined, digits = 2): string {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value || 0);
  }

  protected formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value || 0);
  }

  protected formatDateLabel(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  }

  protected monthLabel(month: number): string {
    return new Date(2026, month - 1, 1).toLocaleString(undefined, { month: 'short' });
  }

  private loadSavedReports(reloadMatching = false): void {
    this.reportsLoading.set(true);
    forkJoin({
      srpcReports: this.service.listSaved('SRPC'),
      nrpcReports: this.service.listSaved('NRPC'),
      nerpcReports: this.service.listSaved('NERPC'),
    }).subscribe({
      next: ({ srpcReports, nrpcReports, nerpcReports }) => {
        const reports = this.sortReports([...(srpcReports || []), ...(nrpcReports || []), ...(nerpcReports || [])]);
        this.savedReports.set(reports);
        this.reportsLoading.set(false);
        if (!this.selectedYear() && reports[0]?.report_year) {
          this.selectedYear.set(String(reports[0].report_year));
        }
        if (!this.selectedMonth() && reports[0]?.report_month) {
          this.selectedMonth.set(String(reports[0].report_month));
        }
        this.applyReportFilter(reloadMatching);
      },
      error: () => {
        this.savedReports.set([]);
        this.reportsLoading.set(false);
        this.matching.set(null);
        this.toast.show('Failed to load saved REA reports', '', 'danger');
      },
    });
  }

  private sortReports(reports: ReaReport[]): ReaReport[] {
    return [...reports].sort((left, right) => {
      const yearDiff = (right.report_year || 0) - (left.report_year || 0);
      if (yearDiff !== 0) {
        return yearDiff;
      }
      const monthDiff = (right.report_month || 0) - (left.report_month || 0);
      if (monthDiff !== 0) {
        return monthDiff;
      }
      return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
    });
  }

  private applyReportFilter(reloadMatching = false): void {
    const filtered = this.filteredReports();
    const nextReportId = filtered.find((report) => report.id === this.selectedReportId())?.id || filtered[0]?.id || '';
    this.selectedReportId.set(nextReportId);
    if (nextReportId && (reloadMatching || !this.matching() || this.matching()?.report.id !== nextReportId)) {
      this.loadMatching();
    } else if (!nextReportId) {
      this.matching.set(null);
      this.scheduleChartRender();
    }
  }

  private loadMatching(): void {
    const reportId = this.selectedReportId();
    if (!reportId) {
      return;
    }
    this.matchingLoading.set(true);
    this.page.set(1);
    this.service.getMatching(reportId).subscribe({
      next: (detail) => {
        this.matching.set(detail);
        this.matchingLoading.set(false);
        this.scheduleChartRender();
      },
      error: (error) => {
        this.matching.set(null);
        this.matchingLoading.set(false);
        this.scheduleChartRender();
        const detail = error?.error?.detail || 'Failed to load REA matching';
        this.toast.show(detail, '', 'danger');
      },
    });
  }

  private scheduleChartRender(): void {
    setTimeout(() => this.renderChart());
  }

  private renderChart(): void {
    if (!this.comparisonCanvas) {
      return;
    }
    const detail = this.matching();
    this.comparisonChart?.destroy();
    if (!detail?.daily_points?.length) {
      return;
    }

    const labels = detail.daily_points.map((point) => this.formatDateLabel(point.date));
    this.comparisonChart = new Chart(this.comparisonCanvas.nativeElement, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Scheduled (MWh)',
            data: detail.daily_points.map((point) => point.scheduled_mwh),
            backgroundColor: '#154787',
            borderRadius: 3,
            barPercentage: 0.78,
            categoryPercentage: 0.58,
          },
          {
            label: 'Actual REA (MWh)',
            data: detail.daily_points.map((point) => point.actual_mwh),
            backgroundColor: '#1f70cb',
            borderRadius: 3,
            barPercentage: 0.78,
            categoryPercentage: 0.58,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 8,
              boxHeight: 8,
              padding: 18,
              color: '#243649',
              font: {
                size: 12,
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: '#334a62',
              font: {
                size: 12,
              },
            },
          },
          y: {
            beginAtZero: true,
            grid: {
              color: '#e8edf5',
            },
            ticks: {
              color: '#334a62',
              font: {
                size: 12,
              },
            },
          },
        },
      },
    });
  }
}
